import React from 'react';
import { Box, Typography } from '@mui/material';

import { formatPercentage } from '@shared/helpers/number';

import { CIRCLE_COLORS } from '../config';
import { VIEWBOX_W, VIEWBOX_H, RADIUS, getCenters, layoutDots, circleTitleAnchor, toPercent } from '../helpers/venn';
import { AssertionDot } from './AssertionDot';

type Props = {
  data: Matches.CategoryData;
};

export const VennDiagram: React.FC<Props> = ({ data }) => {
  const centers = React.useMemo(() => getCenters(data.circles.length), [data.circles.length]);
  const dots = React.useMemo(() => layoutDots(data), [data]);

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
      }}
    >
      <Box
        component="svg"
        viewBox={`0 0 ${VIEWBOX_W} ${VIEWBOX_H}`}
        sx={{ width: '100%', height: '100%', display: 'block' }}
      >
        {data.circles.map((circle, index) => (
          <circle
            key={`fill-${circle.id}`}
            cx={centers[index].x}
            cy={centers[index].y}
            r={RADIUS}
            fill={CIRCLE_COLORS[index]}
            fillOpacity={0.16}
            stroke={CIRCLE_COLORS[index]}
            strokeWidth={2}
          />
        ))}
      </Box>

      {data.circles.map((circle, index) => {
        const anchor = circleTitleAnchor(index, data.circles.length);
        return (
          <Box
            key={`title-${circle.id}`}
            sx={{
              position: 'absolute',
              left: toPercent(anchor.x, VIEWBOX_W),
              top: toPercent(anchor.y, VIEWBOX_H),
              transform: anchor.below ? 'translate(-50%, 0)' : 'translate(-50%, -100%)',
              width: '36%',
              textAlign: 'center',
              pointerEvents: 'none',
            }}
          >
            <Typography
              sx={{
                color: '#fff',
                fontWeight: 700,
                lineHeight: 1.15,
                fontSize: { xs: 13, sm: 16 },
              }}
            >
              {circle.label}
            </Typography>
            <Typography
              sx={{
                color: 'rgba(255, 255, 255, 0.6)',
                fontSize: { xs: 11, sm: 13 },
              }}
            >
              {formatPercentage(circle.matchValue)} match
            </Typography>
          </Box>
        );
      })}

      {dots.map(dot => (
        <AssertionDot
          key={dot.id}
          dot={dot}
          left={toPercent(dot.x, VIEWBOX_W)}
          top={toPercent(dot.y, VIEWBOX_H)}
        />
      ))}
    </Box>
  );
};
